// TreeControls — top control bar for the Org Tree tab.
//
// Layout (left-to-right):
//   [ search field with autocomplete ]  [ Centre on me ]  [ Expand all ]  [ Collapse all ]
//
// Search behaviour
//   - The input is debounced ~150 ms via `useDebouncedValue` so we don't
//     re-rank on every keystroke.
//   - Ranking: prefix matches first (case-insensitive), then substring
//     matches; ties keep original order. Capped at MAX_RESULTS rows.
//   - Each result row shows the display name (bold) plus the job title in
//     muted text.
//   - Click or Enter on a highlighted row emits `onNavigateToNode(id)` and
//     closes the dropdown.
//
// Keyboard
//   - ArrowDown / ArrowUp move the active row within the dropdown.
//   - Enter selects the active row (no-op when the dropdown is empty).
//   - Esc clears the field and closes the dropdown.
//   - The buttons remain reachable via Tab; the search input does not trap
//     focus.
//
// "Centre on me"
//   - Disabled (with a title tooltip) when `currentUserUpn` is null. Mirrors
//     the My-Team pill's disabled treatment in `OrgGraphFilters`.
//
// All callbacks are required (controls are dumb).
//
// See specs/entra-org-tree/us5-search-controls.md.

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import type { IOrgGraphNode } from '@skystone/shared';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from './hooks/useDebouncedValue';

const SEARCH_DEBOUNCE_MS = 150;
const MAX_RESULTS = 10;
const NO_HIGHLIGHT = -1;

export interface TreeControlsProps {
  /** All nodes from the source data — searched by displayName. */
  nodes: readonly IOrgGraphNode[];
  /** UPN of the signed-in user, or null when they have no Entra row. */
  currentUserUpn: string | null;
  /** Search-result selection: jump to + highlight this node. */
  onNavigateToNode: (nodeId: string) => void;
  /** Pan + zoom to the current user's card. */
  onCentreOnMe: () => void;
  /** Expand every collapsed subtree. */
  onExpandAll: () => void;
  /** Collapse every subtree (the hook protects the manager chain). */
  onCollapseAll: () => void;
  className?: string;
}

/**
 * Rank `nodes` for the supplied query — prefix matches first, then substring
 * matches. Case-insensitive. Capped at MAX_RESULTS.
 */
export function rankMatches(
  query: string,
  nodes: readonly IOrgGraphNode[],
): IOrgGraphNode[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [];
  const prefix: IOrgGraphNode[] = [];
  const contains: IOrgGraphNode[] = [];
  for (const n of nodes) {
    const name = n.displayName.toLowerCase();
    if (name.startsWith(q)) {
      prefix.push(n);
    } else if (name.includes(q)) {
      contains.push(n);
    }
  }
  return [...prefix, ...contains].slice(0, MAX_RESULTS);
}

export function TreeControls({
  nodes,
  currentUserUpn,
  onNavigateToNode,
  onCentreOnMe,
  onExpandAll,
  onCollapseAll,
  className,
}: Readonly<TreeControlsProps>): JSX.Element {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(NO_HIGHLIGHT);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxId = useId();

  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const results = useMemo(
    () => rankMatches(debouncedQuery, nodes),
    [debouncedQuery, nodes],
  );

  // Reset the active row whenever the result set identity changes so a stale
  // index doesn't point past the end of a new (shorter) list.
  useEffect(() => {
    setActiveIndex(results.length > 0 ? 0 : NO_HIGHLIGHT);
  }, [results]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(NO_HIGHLIGHT);
  }, []);

  const handleSelect = useCallback(
    (nodeId: string) => {
      onNavigateToNode(nodeId);
      setQuery('');
      closeDropdown();
    },
    [onNavigateToNode, closeDropdown],
  );

  const handleQueryChange = useCallback((next: string) => {
    setQuery(next);
    setIsOpen(next.trim().length > 0);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setQuery('');
        closeDropdown();
        return;
      }
      if (results.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setIsOpen(true);
        setActiveIndex((prev) => {
          const next = prev + 1;
          return next >= results.length ? 0 : next;
        });
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setIsOpen(true);
        setActiveIndex((prev) => {
          const next = prev - 1;
          return next < 0 ? results.length - 1 : next;
        });
        return;
      }
      if (event.key === 'Enter') {
        if (activeIndex >= 0 && activeIndex < results.length) {
          event.preventDefault();
          handleSelect(results[activeIndex].id);
        }
      }
    },
    [results, activeIndex, handleSelect, closeDropdown],
  );

  const hasCurrentUser = currentUserUpn !== null;
  const showDropdown = isOpen && debouncedQuery.trim().length > 0;
  const activeOptionId =
    activeIndex >= 0 && activeIndex < results.length
      ? `${listboxId}-option-${activeIndex}`
      : undefined;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-full border border-input bg-background px-3 py-2 shadow-sm',
        className,
      )}
      data-testid="tree-controls"
    >
      <div className="relative w-[280px]">
        <label className="sr-only" htmlFor={`${listboxId}-input`}>
          Search people in the tree
        </label>
        <input
          ref={inputRef}
          id={`${listboxId}-input`}
          type="search"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          placeholder="Search people…"
          value={query}
          data-testid="tree-controls-search-input"
          onChange={(event) => handleQueryChange(event.target.value)}
          onFocus={() => {
            if (query.trim().length > 0) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className={cn(
            'h-9 w-full rounded-full border border-input bg-background px-4 text-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
        />
        {showDropdown && (
          <ul
            id={listboxId}
            role="listbox"
            data-testid="tree-controls-search-results"
            className="absolute left-0 right-0 top-full z-10 mt-1 max-h-80 overflow-auto rounded-md border border-border bg-background shadow-md"
          >
            {results.length === 0 ? (
              <li
                role="option"
                aria-selected={false}
                aria-disabled
                className="px-3 py-2 text-sm text-muted-foreground"
                data-testid="tree-controls-search-empty"
              >
                No matches
              </li>
            ) : (
              results.map((node, index) => {
                const isActive = index === activeIndex;
                return (
                  <li
                    key={node.id}
                    id={`${listboxId}-option-${index}`}
                    role="option"
                    aria-selected={isActive}
                    data-testid="tree-controls-search-result"
                    data-upn={node.id}
                    // Use onMouseDown so click fires before the input blurs
                    // and closes the dropdown.
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleSelect(node.id);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      'cursor-pointer px-3 py-2 text-sm',
                      isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60',
                    )}
                  >
                    <span className="font-semibold">{node.displayName}</span>
                    {node.jobTitle !== null && (
                      <span className="ml-2 text-muted-foreground">
                        {node.jobTitle}
                      </span>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!hasCurrentUser}
        title={hasCurrentUser ? undefined : 'Current user not found in Entra directory'}
        data-testid="tree-controls-centre-on-me"
        onClick={onCentreOnMe}
      >
        Centre on me
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="tree-controls-expand-all"
        onClick={onExpandAll}
      >
        Expand all
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="tree-controls-collapse-all"
        onClick={onCollapseAll}
      >
        Collapse all
      </Button>
    </div>
  );
}
